import { useState, useCallback, useEffect } from 'react'
import {
  Button,
  Icon,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  VStack,
  HStack,
  Text,
  Input,
  useDisclosure,
  useColorMode,
  Box,
  Divider,
  Link,
} from '@chakra-ui/react'
import { ExternalLink, Key, Sparkles } from 'lucide-react'
import { toast } from 'sonner'
import { getStoredApiKey, setStoredApiKey } from '@/lib/storage/local-storage'
import CursorIcon from '@lobehub/icons/es/Cursor/components/Mono'
import { generateApiKey, validateApiKey } from '@/lib/api/keys'

interface CursorConnectButtonProps {
  variant?: 'full' | 'compact'
  label?: string
  noIcon?: boolean
}

/**
 * Generate a Cursor deeplink URL for one-click MCP installation
 * @see https://cursor.com/docs/context/mcp/install-links
 */
function generateCursorDeeplink(apiKey: string): string {
  // Cursor deeplink expects just the server config (not wrapped in mcpServers)
  // The name param becomes the key, and config is the value
  // See: https://cursor.com/docs/context/mcp/install-links
  const config = {
    url: `${window.location.origin}/api/mcp`,
    headers: {
      Authorization: `Bearer ${apiKey}`,
    },
  }

  const encoded = btoa(JSON.stringify(config))
  return `cursor://anysphere.cursor-deeplink/mcp/install?name=myrkup&config=${encoded}`
}

export function CursorConnectButton({ variant = 'full', label, noIcon }: CursorConnectButtonProps) {
  const { colorMode } = useColorMode()
  const isDark = colorMode === 'dark'
  const { isOpen, onOpen, onClose } = useDisclosure()

  const [storedKey, setStoredKey] = useState<string | null>(null)
  const [inputKey, setInputKey] = useState('')
  const [isValidating, setIsValidating] = useState(false)
  const [isGenerating, setIsGenerating] = useState(false)

  // Load stored API key on mount
  useEffect(() => {
    setStoredKey(getStoredApiKey())
  }, [])

  // Listen for API key changes
  useEffect(() => {
    const handleKeyChange = () => {
      setStoredKey(getStoredApiKey())
    }
    window.addEventListener('apikey-changed', handleKeyChange)
    return () => window.removeEventListener('apikey-changed', handleKeyChange)
  }, [])

  const handleConnectClick = useCallback(() => {
    if (storedKey) {
      // Direct connect with stored key
      window.location.href = generateCursorDeeplink(storedKey)
    } else {
      // Open modal for key entry/generation
      onOpen()
    }
  }, [storedKey, onOpen])

  const handleValidateAndConnect = useCallback(async () => {
    if (!inputKey.trim()) {
      toast.error('Please enter an API key')
      return
    }

    setIsValidating(true)
    try {
      const result = await validateApiKey(inputKey.trim())
      if (result.valid) {
        setStoredApiKey(inputKey.trim())
        setStoredKey(inputKey.trim())
        window.dispatchEvent(new Event('apikey-changed'))
        onClose()
        // Navigate to Cursor deeplink
        window.location.href = generateCursorDeeplink(inputKey.trim())
      } else {
        toast.error('Invalid API key')
      }
    } catch {
      toast.error('Failed to validate API key')
    } finally {
      setIsValidating(false)
    }
  }, [inputKey, onClose])

  const handleGenerateAndConnect = useCallback(async () => {
    setIsGenerating(true)
    try {
      const result = await generateApiKey({})
      if (result.apiKey) {
        setStoredApiKey(result.apiKey)
        setStoredKey(result.apiKey)
        window.dispatchEvent(new Event('apikey-changed'))
        toast.success('Account created!')
        onClose()
        // Navigate to Cursor deeplink
        window.location.href = generateCursorDeeplink(result.apiKey)
      }
    } catch {
      toast.error('Failed to create account')
    } finally {
      setIsGenerating(false)
    }
  }, [onClose])

  return (
    <>
      <Button
        onClick={handleConnectClick}
        size={variant === 'compact' ? 'sm' : 'md'}
        variant="outline"
        borderColor={isDark ? 'stone.600' : 'stone.300'}
        color={isDark ? 'stone.200' : 'stone.700'}
        leftIcon={noIcon ? undefined : <CursorIcon size={16} />}
        _hover={{
          bg: isDark ? 'stone.700' : 'stone.100',
          borderColor: isDark ? 'stone.500' : 'stone.400',
        }}
      >
        {label ?? (variant === 'compact' ? 'Cursor' : 'Connect to Cursor')}
      </Button>

      {/* Setup Modal for users without stored API key */}
      <Modal isOpen={isOpen} onClose={onClose} size="md" isCentered>
        <ModalOverlay />
        <ModalContent bg={isDark ? 'stone.800' : 'white'}>
          <ModalHeader>
            <HStack spacing={2}>
              <CursorIcon size={20} />
              <Text>Connect Myrkup to Cursor</Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={5} align="stretch">
              <Text fontSize="sm" color={isDark ? 'stone.400' : 'stone.600'}>
                Connect your Myrkup account to Cursor IDE to create and manage
                encrypted markdown shares directly from your AI assistant.
              </Text>

              {/* Option 1: Use existing key */}
              <Box>
                <Text fontSize="sm" fontWeight="medium" mb={2}>
                  Sign in with existing API key
                </Text>
                <HStack>
                  <Input
                    placeholder="mk_..."
                    value={inputKey}
                    onChange={(e) => setInputKey(e.target.value)}
                    size="sm"
                    fontFamily="mono"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleValidateAndConnect()
                    }}
                  />
                  <Button
                    onClick={handleValidateAndConnect}
                    isLoading={isValidating}
                    size="sm"
                    colorScheme="brand"
                    leftIcon={<Icon as={Key} boxSize={3} />}
                  >
                    Connect
                  </Button>
                </HStack>
              </Box>

              <HStack>
                <Divider />
                <Text fontSize="xs" color={isDark ? 'stone.500' : 'stone.400'} whiteSpace="nowrap">
                  or
                </Text>
                <Divider />
              </HStack>

              {/* Option 2: Generate new key */}
              <Box>
                <Text fontSize="sm" fontWeight="medium" mb={2}>
                  Create a new anonymous account
                </Text>
                <Button
                  onClick={handleGenerateAndConnect}
                  isLoading={isGenerating}
                  size="sm"
                  variant="outline"
                  w="100%"
                  leftIcon={<Icon as={Sparkles} boxSize={4} />}
                >
                  Create account & connect
                </Button>
                <Text fontSize="xs" color={isDark ? 'stone.500' : 'stone.400'} mt={2}>
                  No email required. Your API key is stored locally.
                </Text>
              </Box>
            </VStack>
          </ModalBody>
          <ModalFooter justifyContent="space-between">
            <Link
              href="/docs/mcp"
              target="_blank"
              fontSize="xs"
              color={isDark ? 'stone.500' : 'stone.400'}
              display="flex"
              alignItems="center"
              gap={1}
            >
              Learn more about MCP
              <Icon as={ExternalLink} boxSize={3} />
            </Link>
            <Button variant="ghost" onClick={onClose} size="sm">
              Cancel
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  )
}
